import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft,
  ShieldAlert,
  Bookmark,
  BookmarkCheck,
  Share2,
  ChevronDown,
  MessageSquareQuote,
  Activity,
  Loader2,
} from "lucide-react";
import { NavBar } from "../components/NavBar";
import { Gauge } from "../components/Gauge";
import { analysisStore, scoreColor } from "../lib/store";
import { getHistory, saveHistory } from "../lib/api";

function Collapsible({ title, icon: Icon, defaultOpen = false, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "18px 22px",
          background: "transparent",
          border: "none",
          color: "var(--on-surface)",
          cursor: "pointer",
        }}
      >
        <Icon size={18} color="var(--brand)" />
        <span style={{ fontWeight: 600, fontSize: 15.5, flex: 1, textAlign: "left" }}>{title}</span>
        <ChevronDown
          size={18}
          color="var(--muted)"
          style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}
        />
      </button>
      {open && <div style={{ padding: "0 22px 22px" }}>{children}</div>}
    </div>
  );
}

function TraitCard({ label, name, score, traits }) {
  const accent = scoreColor(score);
  return (
    <div className="card card-pad">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ width: 9, height: 9, borderRadius: 5, background: accent }} />
        <span className="eyebrow">{label}</span>
        <span style={{ fontWeight: 600, fontSize: 15, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name}
        </span>
        <span className="gauge-num" style={{ fontSize: 24, color: accent }}>
          {score}
        </span>
      </div>
      {traits.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {traits.map((t, i) => (
            <span key={i} className="trait-pill" data-testid={`trait-${label}-${i}`}>
              <ShieldAlert size={12} /> {t}
            </span>
          ))}
        </div>
      ) : (
        <span style={{ color: "var(--muted)", fontSize: 13 }}>No manipulative tactics detected.</span>
      )}
    </div>
  );
}

export default function Scorecard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const store = analysisStore.get();

  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getHistory(id)
      .then(setRemote)
      .catch(() => setRemote(null))
      .finally(() => setLoading(false));
  }, [id]);

  const result = remote ? remote.result : store.result;
  const nameA = remote ? remote.person_a_name : store.personAName;
  const nameB = remote ? remote.person_b_name : store.personBName;
  const fromHistory = !!id;

  const winner = useMemo(() => {
    if (!result) return null;
    const a = result.person_a.narcissism_score;
    const b = result.person_b.narcissism_score;
    if (Math.abs(a - b) < 5) return { tie: true };
    return { tie: false, name: a > b ? nameA : nameB };
  }, [result, nameA, nameB]);

  const onSave = async () => {
    if (!result || saveState !== "idle") return;
    setSaveState("saving");
    try {
      const item = await saveHistory({
        person_a_name: nameA,
        person_b_name: nameB,
        result,
        source_mode: store.sourceMode,
        source_text_preview: store.sourceTextPreview,
      });
      analysisStore.set({ savedId: item.id });
      setSaveState("saved");
      toast.success("Saved to your history");
    } catch (e) {
      setSaveState("idle");
      toast.error(e.message || "Failed to save");
    }
  };

  const onShare = async () => {
    if (!result) return;
    const msg =
      `NarcCheck AI Scorecard\n` +
      `${nameA}: ${result.person_a.narcissism_score}/100\n` +
      `${nameB}: ${result.person_b.narcissism_score}/100\n\n` +
      `Verdict: ${result.verdict}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "NarcCheck AI Scorecard", text: msg });
      } else {
        await navigator.clipboard.writeText(msg);
        toast.success("Scorecard copied to clipboard");
      }
    } catch {}
  };

  const gaugeSize = typeof window !== "undefined" && window.innerWidth < 440 ? 130 : 168;

  if (loading) {
    return (
      <>
        <NavBar />
        <div style={{ display: "grid", placeItems: "center", padding: 120 }}>
          <Loader2 className="spin" size={28} color="var(--brand)" />
        </div>
      </>
    );
  }

  if (!result) {
    return (
      <>
        <NavBar />
        <div style={{ display: "grid", placeItems: "center", padding: 100, gap: 16 }}>
          <p style={{ color: "var(--muted)" }}>No analysis loaded.</p>
          <button className="btn btn-primary" onClick={() => navigate("/app")}>
            Back to analyser
          </button>
        </div>
      </>
    );
  }

  const q = [
    ...result.person_a.notable_quotes.map((t) => ({ who: nameA, t })),
    ...result.person_b.notable_quotes.map((t) => ({ who: nameB, t })),
  ];

  return (
    <>
      <NavBar />
      <main className="container-nc" style={{ paddingTop: 28, paddingBottom: 90, maxWidth: 880 }}>
        <button
          className="btn btn-ghost"
          onClick={() => navigate(fromHistory ? "/history" : "/app")}
          data-testid="back-btn"
          style={{ marginBottom: 22 }}
        >
          <ChevronLeft size={16} /> Back
        </button>

        {/* Gauges */}
        <div
          className="card card-pad fade-up"
          data-testid="scorecard-gauges"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "clamp(18px, 6vw, 90px)",
            flexWrap: "wrap",
            padding: "34px 18px",
          }}
        >
          <Gauge value={result.person_a.narcissism_score} name={nameA} delay={0.1} size={gaugeSize} />
          <Gauge value={result.person_b.narcissism_score} name={nameB} delay={0.25} size={gaugeSize} />
        </div>

        {/* Verdict */}
        <div
          className="card card-pad fade-up"
          data-testid="verdict-card"
          style={{ marginTop: 20, borderColor: "var(--brand)", animationDelay: "0.1s" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "var(--brand-tertiary)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <ShieldAlert size={15} color="var(--brand)" />
            </span>
            <span className="eyebrow" style={{ color: "var(--brand)" }}>
              {winner?.tie ? "Tie — mutual dynamics" : `Leading: ${winner?.name}`}
            </span>
          </div>
          <p style={{ color: "var(--on-surface-2)", fontSize: 15.5, lineHeight: 1.6, margin: 0 }}>
            {result.verdict}
          </p>
        </div>

        {/* Trait cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }} className="trait-grid">
          <TraitCard
            label="Person A"
            name={nameA}
            score={result.person_a.narcissism_score}
            traits={result.person_a.key_traits_detected}
          />
          <TraitCard
            label="Person B"
            name={nameB}
            score={result.person_b.narcissism_score}
            traits={result.person_b.key_traits_detected}
          />
        </div>

        {/* Quotes */}
        {q.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <Collapsible title="Notable Quotes" icon={MessageSquareQuote} defaultOpen testid="quotes-section">
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {q.map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 14 }}>
                    <div style={{ width: 2, background: "var(--brand)", borderRadius: 2 }} />
                    <div>
                      <div className="eyebrow" style={{ color: "var(--brand)", marginBottom: 3 }}>
                        {item.who}
                      </div>
                      <div style={{ color: "var(--on-surface-2)", fontStyle: "italic", lineHeight: 1.5 }}>
                        “{item.t}”
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Collapsible>
          </div>
        )}

        {/* Breakdown */}
        {result.breakdown && (
          <div style={{ marginTop: 16 }}>
            <Collapsible title="Chronological Breakdown" icon={Activity} testid="breakdown-section">
              <p style={{ color: "var(--on-surface-2)", fontSize: 15, lineHeight: 1.65, whiteSpace: "pre-wrap", margin: 0 }}>
                {result.breakdown}
              </p>
            </Collapsible>
          </div>
        )}

        {/* Actions */}
        {!fromHistory && (
          <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
            <button
              className="btn btn-ghost btn-lg"
              style={{ flex: 1, minWidth: 180 }}
              onClick={onSave}
              disabled={saveState !== "idle"}
              data-testid="save-btn"
            >
              {saveState === "saving" ? (
                <Loader2 size={18} className="spin" />
              ) : saveState === "saved" ? (
                <BookmarkCheck size={18} color="var(--success)" />
              ) : (
                <Bookmark size={18} />
              )}
              {saveState === "saved" ? "Saved" : "Save to History"}
            </button>
            <button
              className="btn btn-primary btn-lg"
              style={{ flex: 1, minWidth: 180 }}
              onClick={onShare}
              data-testid="share-btn"
            >
              <Share2 size={18} /> Share Scorecard
            </button>
          </div>
        )}
      </main>
    </>
  );
}
